import { AfterViewChecked, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { from, fromEvent, Subject, Subscription } from 'rxjs';
import { auditTime, concatMap, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MiaV3Service } from '../services/mia-v3.service';
import { SocketService } from '../services/socket.service';
import { AutorizacaoService } from '../services/autorizacao.service';
import { custoEmReaisFormatado as formateCustoEmReais } from './custo-ia';
import {
  corDoAvatar, iniciaisDoContato, nomeDoContato,
  formateTelefone as formateTelefoneDaMia,
  telefoneDoContato as telefoneParaCadastro
} from './contato-mia';

interface ItemChat {
  tipo: 'separador' | 'turno';
  label?: string;
  turno?: any;
}

/** Recado de voz de uma bolha: onda desenhada e até onde a reprodução chegou. */
interface AudioDaBolha {
  url: string;
  /** Alturas em % das barras; vazio = navegador não decodificou (traço liso). */
  onda: number[];
  /** Índice da última barra já reproduzida (-1 = nada). */
  tocado: number;
  /** Do arquivo decodificado, ou do metadata do <audio> quando ele falha. */
  duracaoSegundos: number;
}

// onda do áudio na bolha: mesma contagem de fatias do player do simulador
const FATIAS_DA_ONDA = 32;
// piso das barras — sem ele o silêncio some do desenho
const ALTURA_MINIMA_DA_BARRA = 14;
// acima disso não vale baixar o arquivo só p/ desenhar: a bolha fica no traço liso
const LIMITE_PARA_DESENHAR_A_ONDA = 4 * 1024 * 1024;
// 8 kHz mono basta p/ o desenho e evita segurar PCM enorme na memória
const TAXA_PARA_DECODIFICAR = 8000;

// listas longas colapsam: mostra 6 e o botão "Ver todas"; até 8 cabem sem colapsar
const OPCOES_VISIVEIS_COLAPSADO = 6;
const MAXIMO_DE_OPCOES_SEM_COLAPSAR = 8;

// acima disso a latência aparece no badge (âmbar) — abaixo, só no detalhe expandido
const LATENCIA_LENTA_MS = 5000;
/** Tempo total (cliente → resposta enviada) a partir do qual o selo ⏱ ganha destaque. */
const TEMPO_TOTAL_LENTO_MS = 10000;

@Component({
  selector: 'app-conversas-mia-v3',
  templateUrl: './conversas-mia-v3.component.html',
  styleUrls: ['./conversas-mia-v3.component.scss']
})
export class ConversasMiaV3Component implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('corpoChat') corpoChat: ElementRef;
  @ViewChild('campoResposta') campoResposta: ElementRef;

  conversas: any[] = [];
  conversaSelecionada: any = null;
  itensChat: ItemChat[] = [];
  /** Carrinho montado pela Mia nesta conversa (null sem itens) — card acima da transcrição. */
  carrinhoConversa: any = null;
  carrinhoExpandido = true;
  // agregado dos turnos da conversa aberta (modelos usados + tokens somados)
  resumoTelemetria: { modelos: string[]; entrada: number; saida: number; cache: number; cacheEscrita: number } | null = null;

  carregandoLista = true;
  carregandoChat = false;
  busca = '';
  aoVivo = false;
  filtro: 'todas' | 'atendente' | 'nao_converteu' | 'venderam' = 'todas';

  // Período da lista (última atividade da conversa). A fila de atendimento
  // ignora o período: fila é fila, gente esperando não pode sumir por data.
  periodoDias = 7;
  periodoPersonalizado = false;
  periodoDe = '';
  periodoAte = '';
  resposta = '';
  alterandoEstado = false;
  alterandoBloqueio = false;
  enviandoResposta = false;
  erroAcao = '';
  ultimaMensagemClienteEm: Date | null = null;
  agora = Date.now();
  respostasRapidas = [
    { rotulo: 'Já verifico', texto: 'Um momento, por favor. Já vou verificar isso para você.' },
    { rotulo: 'Obrigado por aguardar', texto: 'Obrigado por aguardar! Já estou com seu atendimento.' },
    { rotulo: 'Pode me confirmar?', texto: 'Pode me confirmar essa informação, por favor?' },
    { rotulo: 'Mais alguma coisa?', texto: 'Posso ajudar com mais alguma coisa?' },
  ];

  private busca$ = new Subject<string>();
  private atualizacoes$ = new Subject<void>();
  private assinaturas: Subscription[] = [];
  private leiturasEmAndamento = new Set<number>();
  private deveRolarParaOFim = false;
  private rascunhos: { [conversaId: string]: string } = {};
  private usuario: any = {};
  // Estado dos players de áudio por URL do arquivo. A lista do chat é remontada
  // a cada atualização silenciosa; guardar aqui mantém a onda desenhada e a
  // posição da reprodução em vez de tudo voltar do zero a cada sincronização.
  private audiosPorUrl = new Map<string, AudioDaBolha>();
  // As ondas são decodificadas uma de cada vez: a conversa pode ter vários
  // recados e não vale disparar uma rajada de downloads nem segurar vários
  // buffers decodificados ao mesmo tempo.
  private filaDeOndas: Promise<void> = Promise.resolve();
  private frameDaReproducao = 0;
  /** Player que instalou o rAF vigente — só ele pode cancelá-lo. */
  private donoDaReproducao: HTMLAudioElement | null = null;

  constructor(private miaV3Service: MiaV3Service, private socketService: SocketService,
              private autorizacaoService: AutorizacaoService, private zone: NgZone,
              private router: Router, private rota: ActivatedRoute) {}

  /** Telemetria de IA (modelo, tokens, custo, cache) é assunto interno do MeuCardápio. */
  get ehAdmin(): boolean {
    return !!(this.usuario.admin || this.usuario.superadmin);
  }

  /** O painel linka para cá com o filtro e o período já escolhidos. */
  private apliqueParametrosDaUrl(): void {
    const params = this.rota.snapshot.queryParamMap;

    const filtro = params.get('filtro');
    if (filtro === 'nao_converteu' || filtro === 'venderam' || filtro === 'atendente') {
      this.filtro = filtro;
    }

    const de = params.get('de');
    const ate = params.get('ate');
    if (de && ate) {
      this.periodoPersonalizado = true;
      this.periodoDe = de;
      this.periodoAte = ate;
      return;
    }

    const dias = Number(params.get('dias'));
    if ([1, 7, 30].includes(dias)) this.periodoDias = dias;
  }

  /** Abre o simulador (Testar Mia V3), que já começa uma conversa nova. */
  novaConversa() {
    this.router.navigateByUrl('/admin/mia-v3/testar');
  }

  ngOnInit(): void {
    this.usuario = this.autorizacaoService.getUser() || {};
    this.apliqueParametrosDaUrl();
    this.carregueConversas();

    this.assinaturas.push(
      this.busca$.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => this.carregueConversas())
    );

    // Agrupa rajadas de mensagens/status e serializa as consultas para não aplicar respostas fora de ordem.
    this.assinaturas.push(
      this.atualizacoes$.pipe(
        auditTime(200),
        concatMap(() => from(this.sincronizeSilenciosamente()))
      ).subscribe()
    );

    this.assinaturas.push(
      this.socketService.on('mia3:atualizacao').subscribe(() => this.soliciteAtualizacao())
    );
    // Compatibilidade com processos que ainda emitam somente o evento antigo de escalação.
    this.assinaturas.push(
      this.socketService.on('mia3:escalacao').subscribe(() => this.soliciteAtualizacao())
    );

    this.aoVivo = this.socketService.conectado;
    this.assinaturas.push(this.socketService.on('connect').subscribe(() => {
      this.aoVivo = true;
      this.soliciteAtualizacao();
    }));
    this.assinaturas.push(this.socketService.on('disconnect').subscribe(() => this.aoVivo = false));
    this.assinaturas.push(fromEvent(document, 'visibilitychange').subscribe(() => {
      if (!document.hidden) {
        this.agora = Date.now();
        this.soliciteAtualizacao();
      }
    }));
  }

  ngOnDestroy(): void {
    this.assinaturas.forEach(assinatura => assinatura.unsubscribe());
    this.pareDeSeguirAReproducao();
  }

  ngAfterViewChecked(): void {
    if (this.deveRolarParaOFim && this.corpoChat) {
      this.corpoChat.nativeElement.scrollTop = this.corpoChat.nativeElement.scrollHeight;
      this.deveRolarParaOFim = false;
    }
  }

  aoDigitarBusca() {
    this.busca$.next(this.busca);
  }

  carregueConversas(silencioso = false): Promise<void> {
    if (!silencioso) this.carregandoLista = true;

    return this.miaV3Service.obtenhaConversas(this.busca, this.periodoAtual()).then(conversas => {
      this.conversas = conversas;
      if (this.conversaSelecionada) {
        const selecionadaId = Number(this.conversaSelecionada.id);
        const atualizada = conversas.find(conversa => Number(conversa.id) === selecionadaId);
        if (atualizada) this.conversaSelecionada = { ...this.conversaSelecionada, ...atualizada };
      }
      this.carregandoLista = false;
    }).catch(() => {
      this.carregandoLista = false;
    });
  }

  get conversasVisiveis(): any[] {
    if (this.filtro === 'atendente') {
      return this.conversas.filter(conversa => conversa.estado === 'PAUSADA_ATENDENTE');
    }
    if (this.filtro === 'nao_converteu') {
      return this.conversas.filter(conversa => this.naoConverteu(conversa));
    }
    if (this.filtro === 'venderam') {
      return this.conversas.filter(conversa =>
        Number(conversa.pedidosGerados) > 0 && !this.ehConversaDeTeste(conversa));
    }
    return this.conversas;
  }

  get quantidadeComAtendente(): number {
    return this.conversas.filter(conversa => conversa.estado === 'PAUSADA_ATENDENTE').length;
  }

  get quantidadeNaoConverteram(): number {
    return this.conversas.filter(conversa => this.naoConverteu(conversa)).length;
  }

  /**
   * Perdeu venda DENTRO do período consultado — a mesma conta do painel.
   * O último desfecho de todos os tempos fazia perda antiga de conversa
   * ativa recentemente aparecer na lista de 7 dias.
   */
  private naoConverteu(conversa: any): boolean {
    return Number(conversa.perdasNoPeriodo) > 0;
  }

  /**
   * Conversa de teste (simulador do painel 'sim:' ou widget 'wdg:'): aparece
   * em "Todas" para acompanhar o teste ao vivo, mas fica fora das visões de
   * venda/perda — mesma regra das métricas do painel, que ignoram esses
   * telefones em todos os números.
   */
  private ehConversaDeTeste(conversa: any): boolean {
    const telefone = String(conversa?.telefone || '');
    return telefone.startsWith('sim:') || telefone.startsWith('wdg:');
  }

  /**
   * Rótulo humano do motivo (mesmos textos do painel) para a etiqueta da linha.
   * O motivo gravado é o do ÚLTIMO episódio: se depois da perda veio outro
   * atendimento (um pós-venda, por exemplo), a etiqueta fica de fora em vez
   * de rotular a perda com o motivo do episódio errado.
   */
  etiquetaMotivo(conversa: any): string | null {
    if (!this.naoConverteu(conversa) || Number(conversa.pedidosGerados) > 0) return null;
    if (conversa.ultimoDesfecho !== 'SEM_PEDIDO') return null;
    const rotulos: { [motivo: string]: string } = {
      ABANDONO_CARRINHO: 'Montou o carrinho e sumiu',
      PAGAMENTO_FALHOU: 'O pagamento não passou',
      FORA_AREA: 'Fora da área de entrega',
      LOJA_FECHADA: 'A loja estava fechada',
      ITEM_INDISPONIVEL: 'Procurou algo que não tem',
      DESISTENCIA: 'Desistiu durante a conversa'
    };
    return rotulos[conversa.ultimoMotivo] || null;
  }

  /** null quando a fila de atendimento está ativa — ela não filtra por data. */
  private periodoAtual(): { de?: string, ate?: string } | undefined {
    if (this.filtro === 'atendente') return undefined;

    if (this.periodoPersonalizado) {
      if (!this.periodoValido) return undefined;
      return { de: `${this.periodoDe} 00:00:00`, ate: `${this.periodoAte} 23:59:59` };
    }

    const inicio = new Date();
    inicio.setHours(0, 0, 0, 0);
    inicio.setDate(inicio.getDate() - (this.periodoDias - 1));
    const doisDigitos = (valor: number) => String(valor).padStart(2, '0');
    const dia = `${inicio.getFullYear()}-${doisDigitos(inicio.getMonth() + 1)}-${doisDigitos(inicio.getDate())}`;
    return { de: `${dia} 00:00:00` };
  }

  get periodoValido(): boolean {
    return !!this.periodoDe && !!this.periodoAte && this.periodoDe <= this.periodoAte;
  }

  selecionePeriodo(dias: number): void {
    if (dias === this.periodoDias && !this.periodoPersonalizado) return;
    this.periodoPersonalizado = false;
    this.periodoDias = dias;
    this.carregueConversas();
  }

  ativePeriodoPersonalizado(): void {
    if (this.periodoPersonalizado) return;
    this.periodoPersonalizado = true;
    const doisDigitos = (valor: number) => String(valor).padStart(2, '0');
    const formate = (data: Date) =>
      `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}`;
    const inicio = new Date();
    inicio.setDate(inicio.getDate() - (this.periodoDias - 1));
    this.periodoDe = formate(inicio);
    this.periodoAte = formate(new Date());
  }

  apliquePeriodo(): void {
    if (!this.periodoValido) return;
    this.carregueConversas();
  }

  rastreieConversa(indice: number, conversa: any): string {
    return `conversa:${conversa?.id ?? indice}`;
  }

  rastreieItemChat(indice: number, item: ItemChat): string {
    if (item.tipo === 'turno') return `turno:${item.turno?.id ?? indice}`;
    return `separador:${item.label ?? indice}`;
  }

  altereFiltro(filtro: 'todas' | 'atendente' | 'nao_converteu' | 'venderam') {
    if (this.filtro === filtro) return;
    const mudaConsulta = filtro === 'atendente' || this.filtro === 'atendente';
    this.filtro = filtro;
    // entrar/sair da fila muda o universo consultado (a fila ignora o período)
    if (mudaConsulta) this.carregueConversas();
  }

  abraConversa(conversa: any) {
    if (this.conversaSelecionada?.id === conversa.id) return;
    this.salveRascunho();
    this.conversaSelecionada = conversa;
    this.resposta = this.rascunhos[String(conversa.id)] || '';
    this.itensChat = [];
    this.ultimaMensagemClienteEm = null;
    this.erroAcao = '';
    this.carregueTurnos(conversa);
  }

  fecheConversa() {
    this.salveRascunho();
    this.conversaSelecionada = null;
    this.itensChat = [];
    this.ultimaMensagemClienteEm = null;
    this.erroAcao = '';
  }

  private carregueTurnos(conversa: any, silencioso = false): Promise<void> {
    const acompanhandoOFim = !silencioso || this.estaNoFimDoChat();
    if (!silencioso) {
      this.carregandoChat = true;
      this.resumoTelemetria = null; // não mostrar o resumo da conversa anterior
      this.carrinhoConversa = null;
    }
    this.carregueCarrinho(conversa);

    return this.miaV3Service.obtenhaTurnos(conversa.id).then(({ conversa: conversaAtualizada, turnos }) => {
      if (!this.conversaSelecionada || Number(this.conversaSelecionada.id) !== Number(conversa.id)) return;
      if (conversaAtualizada) this.atualizeConversa(conversaAtualizada);
      this.itensChat = this.monteItensChat(turnos);
      this.resumoTelemetria = this.calculeResumoTelemetria(turnos);
      const ultimoTurnoCliente = [...turnos].reverse().find(turno => turno.papel === 'cliente');
      this.agora = Date.now();
      this.ultimaMensagemClienteEm = ultimoTurnoCliente?.criadoEm
        ? new Date(ultimoTurnoCliente.criadoEm)
        : null;
      this.carregandoChat = false;
      this.deveRolarParaOFim = acompanhandoOFim;
      this.marqueMensagensVisiveisComoLidas(conversa, turnos);
      if (!silencioso && this.conversaSelecionada.estado === 'PAUSADA_ATENDENTE') {
        setTimeout(() => this.foqueCampoResposta());
      }
    }).catch(() => {
      this.carregandoChat = false;
    });
  }

  // ---- carrinho montado na conversa (para o atendente que assume) ----

  /** Card do carrinho: recarrega junto com os turnos; falha não interrompe a transcrição. */
  private carregueCarrinho(conversa: any) {
    const conversaId = Number(conversa?.id);
    if (!conversaId) return;
    this.miaV3Service.obtenhaCarrinhoDaConversa(conversaId).then((carrinho: any) => {
      if (!this.conversaSelecionada || Number(this.conversaSelecionada.id) !== conversaId) return;
      this.carrinhoConversa = carrinho && carrinho.itens?.length ? carrinho : null;
    }).catch(() => {
      // sem carrinho é o caso comum; erro de rede não merece alerta aqui
    });
  }

  alterneCarrinho() {
    this.carrinhoExpandido = !this.carrinhoExpandido;
  }

  formateReais(valor: number): string {
    return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  quantidadeDoItem(item: any): string {
    if (item?.peso != null) return `${item.peso} ${item.unidadePeso || ''}`.trim();
    return `${item?.quantidade || 0}×`;
  }

  /** Chips das anotações da conversa: entrega/retirada, pagamento, troco, agendamento, cupom. */
  anotacoesDoCarrinho(): string[] {
    const anotacoes = this.carrinhoConversa?.anotacoes;
    if (!anotacoes) return [];
    const chips: string[] = [];
    if (anotacoes.modoEntrega === 'DELIVERY') chips.push('Entrega');
    if (anotacoes.modoEntrega === 'RETIRADA') chips.push('Retirada');
    if (anotacoes.pagamentoPreferido) chips.push(`Pagamento: ${anotacoes.pagamentoPreferido}`);
    if (anotacoes.trocoPreferido === 'SEM') chips.push('Sem troco');
    else if (anotacoes.trocoPreferido) chips.push(`Troco para ${this.formateReais(anotacoes.trocoPreferido)}`);
    if (anotacoes.agendamento?.data) {
      const [ano, mes, dia] = String(anotacoes.agendamento.data).split('-');
      chips.push(`Agendado ${dia}/${mes}${anotacoes.agendamento.hora ? ' às ' + anotacoes.agendamento.hora : ''}`);
    }
    if (anotacoes.cupom) chips.push(`Cupom ${anotacoes.cupom}`);
    return chips;
  }

  carrinhoAtualizadoHa(): string {
    const quando = this.carrinhoConversa?.atualizadoEm ? new Date(this.carrinhoConversa.atualizadoEm).getTime() : 0;
    if (!quando) return '';
    const minutos = Math.max(0, Math.round((this.agora - quando) / 60000));
    if (minutos < 1) return 'agora';
    if (minutos < 60) return `há ${minutos} min`;
    const horas = Math.round(minutos / 60);
    if (horas < 24) return `há ${horas} h`;
    return `há ${Math.round(horas / 24)} d`;
  }

  private soliciteAtualizacao() {
    this.atualizacoes$.next();
  }

  private sincronizeSilenciosamente(): Promise<void> {
    const selecionada = this.conversaSelecionada;
    const consultas: Array<Promise<void>> = [this.carregueConversas(true)];
    if (selecionada && !this.enviandoResposta) consultas.push(this.carregueTurnos(selecionada, true));
    return Promise.all(consultas).then(() => undefined);
  }

  private estaNoFimDoChat(): boolean {
    if (!this.corpoChat) return true;
    const elemento = this.corpoChat.nativeElement;
    return elemento.scrollHeight - elemento.scrollTop - elemento.clientHeight < 80;
  }

  private marqueMensagensVisiveisComoLidas(conversa: any, turnos: any[]) {
    if (document.hidden || this.conversaSelecionada?.somenteLeituraV4) return;

    const conversaId = Number(conversa?.id);
    const temMensagemPendente = turnos.some(turno =>
      turno?.papel === 'cliente' && turno?.wamid && turno?.statusWhatsapp !== 'read'
    );
    if (!conversaId || !temMensagemPendente || this.leiturasEmAndamento.has(conversaId)) return;

    this.leiturasEmAndamento.add(conversaId);
    this.miaV3Service.marqueConversaComoLida(conversaId)
      .catch(() => undefined)
      .finally(() => this.leiturasEmAndamento.delete(conversaId));
  }

  assumaConversa() {
    if (!this.conversaSelecionada || this.conversaSelecionada.somenteLeituraV4 || this.alterandoEstado) return;
    this.alterandoEstado = true;
    this.erroAcao = '';

    this.miaV3Service.assumaConversa(this.conversaSelecionada.id).then((resposta: any) => {
      this.atualizeConversa(resposta?.conversa || { estado: 'PAUSADA_ATENDENTE' });
      this.alterandoEstado = false;
      setTimeout(() => this.foqueCampoResposta());
    }).catch(erro => {
      this.alterandoEstado = false;
      this.erroAcao = this.mensagemDoErro(erro, 'Não foi possível assumir a conversa.');
    });
  }

  /** Contato bloqueado: a Mia não responde este telefone em canal nenhum. */
  contatoBloqueado(): boolean {
    return this.conversaSelecionada?.desativadaParaSempre === true;
  }

  /**
   * Liga/desliga o bloqueio permanente do contato. Otimista na tela (a faixa
   * aparece na hora) e revertido se o servidor recusar — a fonte da verdade
   * continua sendo o estado_chatbot, relido a cada sincronização.
   */
  alterneBloqueioDoContato(desativada: boolean) {
    if (!this.conversaSelecionada || this.conversaSelecionada.somenteLeituraV4 || this.alterandoBloqueio) return;

    const conversaId = this.conversaSelecionada.id;
    const valorAnterior = this.contatoBloqueado();
    if (valorAnterior === desativada) return;

    this.alterandoBloqueio = true;
    this.erroAcao = '';
    this.atualizeConversa({ desativadaParaSempre: desativada });

    this.miaV3Service.altereBloqueioDoContato(conversaId, desativada).then((resposta: any) => {
      if (resposta?.conversa) this.atualizeConversa(resposta.conversa);
      this.alterandoBloqueio = false;
    }).catch(erro => {
      this.atualizeConversa({ desativadaParaSempre: valorAnterior });
      this.alterandoBloqueio = false;
      this.erroAcao = this.mensagemDoErro(erro, desativada
        ? 'Não foi possível desativar a Mia para este contato.'
        : 'Não foi possível reativar a Mia para este contato.');
    });
  }

  devolvaConversa() {
    if (!this.conversaSelecionada || this.conversaSelecionada.somenteLeituraV4 || this.alterandoEstado || this.enviandoResposta) return;
    this.alterandoEstado = true;
    this.erroAcao = '';

    this.miaV3Service.devolvaConversa(this.conversaSelecionada.id).then((resposta: any) => {
      this.atualizeConversa(resposta?.conversa || { estado: 'ATIVA' });
      this.alterandoEstado = false;
    }).catch(erro => {
      this.alterandoEstado = false;
      this.erroAcao = this.mensagemDoErro(erro, 'Não foi possível devolver a conversa para a Mia.');
    });
  }

  envieResposta(textoForcado?: string) {
    if (!this.conversaSelecionada || this.conversaSelecionada.somenteLeituraV4 || this.enviandoResposta || !this.dentroDaJanela24h()) return;

    const texto = String(textoForcado != null ? textoForcado : this.resposta).trim();
    if (!texto) {
      this.foqueCampoResposta();
      return;
    }

    const conversaId = this.conversaSelecionada.id;
    const turnoOtimista = {
      id: `local-${Date.now()}`,
      conversaId,
      papel: 'atendente',
      texto,
      criadoEm: new Date(),
      statusEnvio: 'enviando',
      erroEnvio: '',
    };

    this.adicioneTurnoOtimista(turnoOtimista);
    this.resposta = '';
    this.rascunhos[String(conversaId)] = '';
    this.enviandoResposta = true;
    this.erroAcao = '';
    this.redefinaAlturaCampo();

    this.miaV3Service.respondaConversa(conversaId, texto).then((resposta: any) => {
      turnoOtimista.statusEnvio = resposta?.confirmacaoPendente ? 'aviso' : 'enviado';
      this.enviandoResposta = false;
      if (resposta?.confirmacaoPendente) {
        this.erroAcao = resposta.aviso;
        return;
      }
      if (this.conversaSelecionada?.id === conversaId) {
        this.carregueTurnos(this.conversaSelecionada, true);
        setTimeout(() => this.foqueCampoResposta());
      }
    }).catch(erro => {
      turnoOtimista.statusEnvio = 'erro';
      turnoOtimista.erroEnvio = this.mensagemDoErro(erro, 'Falha ao enviar. Tente novamente.');
      this.enviandoResposta = false;
      if (this.conversaSelecionada?.id === conversaId) {
        this.erroAcao = turnoOtimista.erroEnvio;
        this.deveRolarParaOFim = true;
      } else {
        this.rascunhos[String(conversaId)] = texto;
      }
    });
  }

  tenteNovamente(turno: any) {
    if (!turno || this.enviandoResposta) return;
    this.itensChat = this.itensChat.filter(item => item.turno !== turno);
    this.envieResposta(turno.texto);
  }

  aoPressionarResposta(evento: KeyboardEvent) {
    if (evento.key === 'Enter' && !evento.shiftKey && !(evento as any).isComposing) {
      evento.preventDefault();
      this.envieResposta();
    }
  }

  aoDigitarResposta(evento: any) {
    const campo = evento?.target;
    if (!campo) return;
    campo.style.height = 'auto';
    campo.style.height = `${Math.min(campo.scrollHeight, 132)}px`;
  }

  useRespostaRapida(texto: string) {
    const atual = this.resposta.trim();
    this.resposta = atual ? `${atual}\n${texto}` : texto;
    this.salveRascunho();
    setTimeout(() => {
      this.foqueCampoResposta();
      if (this.campoResposta) this.aoDigitarResposta({ target: this.campoResposta.nativeElement });
    });
  }

  dentroDaJanela24h(): boolean {
    if (!this.ultimaMensagemClienteEm) return false;
    return this.agora - this.ultimaMensagemClienteEm.getTime() < 24 * 60 * 60 * 1000;
  }

  textoDaJanela(): string {
    if (!this.ultimaMensagemClienteEm) return 'Aguardando uma mensagem do cliente';
    const restanteMs = (24 * 60 * 60 * 1000) - (this.agora - this.ultimaMensagemClienteEm.getTime());
    if (restanteMs <= 0) return 'Fora da janela de 24h';
    const horas = Math.floor(restanteMs / (60 * 60 * 1000));
    const minutos = Math.floor((restanteMs % (60 * 60 * 1000)) / (60 * 1000));
    return horas > 0 ? `Janela aberta por mais ${horas}h ${minutos}min` : `Janela aberta por mais ${minutos}min`;
  }

  private adicioneTurnoOtimista(turno: any) {
    const label = this.labelDoDia(turno.criadoEm);
    const ultimoSeparador = [...this.itensChat].reverse().find(item => item.tipo === 'separador');
    if (!ultimoSeparador || ultimoSeparador.label !== label) {
      this.itensChat.push({ tipo: 'separador', label });
    }
    this.prepareOpcoesDaLista(turno);
    this.prepareMidia(turno);
    this.itensChat.push({ tipo: 'turno', turno });
    this.deveRolarParaOFim = true;
  }

  private atualizeConversa(dados: any) {
    this.conversaSelecionada = { ...this.conversaSelecionada, ...dados };
    const indice = this.conversas.findIndex(conversa => Number(conversa.id) === Number(this.conversaSelecionada.id));
    if (indice >= 0) this.conversas[indice] = { ...this.conversas[indice], ...dados };
  }

  private salveRascunho() {
    if (this.conversaSelecionada) {
      this.rascunhos[String(this.conversaSelecionada.id)] = this.resposta;
    }
  }

  private foqueCampoResposta() {
    if (this.campoResposta && !this.campoResposta.nativeElement.disabled) {
      this.campoResposta.nativeElement.focus();
    }
  }

  private redefinaAlturaCampo() {
    setTimeout(() => {
      if (this.campoResposta) this.campoResposta.nativeElement.style.height = 'auto';
    });
  }

  private mensagemDoErro(erro: any, fallback: string): string {
    return erro?.error?.erro || erro?.message || fallback;
  }

  private monteItensChat(turnos: any[]): ItemChat[] {
    const itens: ItemChat[] = [];
    let diaAnterior = '';

    for (const turno of turnos) {
      const dia = this.labelDoDia(turno.criadoEm);
      if (dia !== diaAnterior) {
        itens.push({ tipo: 'separador', label: dia });
        diaAnterior = dia;
      }
      this.prepareOpcoesDaLista(turno);
      this.prepareMidia(turno);
      itens.push({ tipo: 'turno', turno });
    }

    return itens;
  }

  /** Marcadores de figurinha na transcrição — com a figurinha na bolha, o texto some. */
  private static readonly PLACEHOLDERS_FIGURINHA = ['[figurinha]', '[🙂 Figurinha enviada pelo cliente]'];
  /** Vídeo sem legenda: com o player na bolha, o marcador puro some (com legenda o texto fica). */
  private static readonly PLACEHOLDERS_VIDEO = ['[vídeo]', '[🎬 Vídeo enviado pelo cliente]'];
  /** Contato: o card já mostra nome e telefone — o marcador de texto some. */
  private static readonly PREFIXOS_CONTATO = ['[contato]', '[👤 Contato compartilhado', '[👤 Contatos compartilhados'];

  /**
   * JSON da coluna `midia` do turno ([{tipo, arquivo, nome, mime, legenda}])
   * vira as listas que o template renderiza: imagem aparece na bolha (clique
   * abre em tamanho real), figurinha vira imagem solta estilo sticker, áudio e
   * vídeo viram player, contato vira card com download do vCard e documento
   * vira cartão com botão de baixar. O arquivo é servido pela rota autenticada
   * /miav3/midia/:arquivo.
   */
  private prepareMidia(turno: any) {
    turno.imagensMidia = [];
    turno.figurinhasMidia = [];
    turno.audiosMidia = [];
    turno.videosMidia = [];
    turno.contatosMidia = [];
    turno.documentosMidia = [];
    turno.ocultarTexto = false;
    if (!turno.midia) return;

    let midias: any[];
    try {
      midias = JSON.parse(turno.midia);
    } catch {
      return;
    }
    if (!Array.isArray(midias)) return;

    for (const midia of midias) {
      if (!midia?.arquivo) continue;
      const url = `/miav3/midia/${midia.arquivo}`;
      if (midia.tipo === 'imagem') {
        turno.imagensMidia.push({ url, legenda: midia.legenda || '' });
      } else if (midia.tipo === 'figurinha') {
        turno.figurinhasMidia.push({ url });
      } else if (midia.tipo === 'audio') {
        turno.audiosMidia.push(this.estadoDoAudio(url));
      } else if (midia.tipo === 'video') {
        turno.videosMidia.push({ url, mime: midia.mime || '' });
      } else if (midia.tipo === 'contato') {
        const nome = midia.nome || 'Contato';
        turno.contatosMidia.push({
          nome,
          telefone: midia.legenda || '',
          urlDownload: `${url}?baixar=1&nome=${encodeURIComponent(nome + '.vcf')}`,
        });
      } else {
        const nome = midia.nome || 'documento';
        const extensao = String(midia.arquivo.split('.').pop() || '').toUpperCase();
        turno.documentosMidia.push({
          nome,
          extensao,
          urlDownload: `${url}?baixar=1&nome=${encodeURIComponent(nome)}`,
          icone: extensao === 'PDF' ? 'fa-file-pdf' : 'fa-file-alt',
        });
      }
    }

    // mídia renderizada dispensa o marcador de texto na bolha (legenda fica)
    const textoDaBolha = String(turno.corpoExibicao || turno.texto || '').trim();
    turno.ocultarTexto =
      (turno.figurinhasMidia.length && ConversasMiaV3Component.PLACEHOLDERS_FIGURINHA.includes(textoDaBolha)) ||
      (turno.videosMidia.length && ConversasMiaV3Component.PLACEHOLDERS_VIDEO.includes(textoDaBolha)) ||
      (turno.contatosMidia.length &&
        ConversasMiaV3Component.PREFIXOS_CONTATO.some(prefixo => textoDaBolha.startsWith(prefixo)));
  }

  // ==== player do recado de voz (o desenho do WhatsApp, não o do navegador) ====

  /**
   * Estado do player por URL do arquivo. Devolver sempre o MESMO objeto faz o
   * *ngFor reaproveitar o <audio> já montado: a atualização silenciosa da
   * conversa não interrompe o áudio que está tocando nem redesenha a onda.
   */
  private estadoDoAudio(url: string): AudioDaBolha {
    const jaConhecido = this.audiosPorUrl.get(url);
    if (jaConhecido) return jaConhecido;

    const audio: AudioDaBolha = { url, onda: [], tocado: -1, duracaoSegundos: 0 };
    this.audiosPorUrl.set(url, audio);
    this.enfileireAOnda(audio);
    return audio;
  }

  /** Botão redondo da bolha. Um recado de cada vez, como no WhatsApp. */
  toqueOuPause(player: HTMLAudioElement) {
    if (!player.paused) {
      player.pause();
      return;
    }
    if (this.donoDaReproducao && this.donoDaReproducao !== player) this.donoDaReproducao.pause();
    player.play().catch(() => {});
  }

  /** Metadata carregado: duração de verdade quando o arquivo não foi decodificado. */
  anoteADuracao(audio: AudioDaBolha, player: HTMLAudioElement) {
    if (isFinite(player.duration) && player.duration > 0) audio.duracaoSegundos = player.duration;
  }

  /**
   * Marcador da reprodução a 60fps, escrito direto no DOM e FORA do zone.
   *
   * O evento `timeupdate` do <audio> só dispara ~4x por segundo — animar a
   * bolinha por ele a deixa pulando de ponto em ponto. O preenchimento das
   * barras continua no `timeupdate` (degrau ali é o certo, e é o que o WhatsApp
   * faz); só a bolinha precisa de quadro a quadro.
   */
  sigaAReproducao(audio: AudioDaBolha, player: HTMLAudioElement, marcador: HTMLElement) {
    this.pareDeSeguirAReproducao(this.donoDaReproducao);
    this.donoDaReproducao = player;

    this.zone.runOutsideAngular(() => {
      const passo = () => {
        if (player.paused || player.ended) {
          this.frameDaReproducao = 0;
          return;
        }
        marcador.style.left = (this.fracaoTocada(audio, player) * 100).toFixed(2) + '%';
        this.frameDaReproducao = requestAnimationFrame(passo);
      };
      this.frameDaReproducao = requestAnimationFrame(passo);
    });
  }

  /**
   * Pausou/terminou: para o loop — mas só se o player que pausou for o DONO
   * dele. O pause de uma bolha não pode congelar o marcador de outra.
   */
  pareDeSeguirAReproducao(player?: HTMLAudioElement | null) {
    if (player && player !== this.donoDaReproducao) return;
    if (this.frameDaReproducao) cancelAnimationFrame(this.frameDaReproducao);
    this.frameDaReproducao = 0;
    this.donoDaReproducao = null;
  }

  /** Preenche a onda conforme a reprodução anda, como no WhatsApp. */
  marqueAOnda(audio: AudioDaBolha, player: HTMLAudioElement) {
    const fracao = this.fracaoTocada(audio, player);
    // sem onda decodificada o traço é liso, mas a bolinha ainda corre por ele
    const fatias = Math.max(audio.onda.length, 1);
    audio.tocado = fracao > 0 ? Math.round(fracao * (fatias - 1)) : -1;
  }

  /** Fim do recado: volta ao começo, com a onda e o marcador junto. */
  termineAReproducao(audio: AudioDaBolha, player: HTMLAudioElement, marcador: HTMLElement) {
    player.currentTime = 0;
    audio.tocado = -1;
    this.pareDeSeguirAReproducao(player);
    marcador.style.left = '0%';
  }

  /** Clique na onda anda no áudio, como arrastar a bolinha no WhatsApp. */
  busqueNoAudio(evento: MouseEvent, audio: AudioDaBolha, player: HTMLAudioElement, marcador: HTMLElement) {
    const total = this.duracaoDoAudio(audio, player);
    const area = (evento.currentTarget as HTMLElement).getBoundingClientRect();
    if (!total || !area.width) return;

    const fracao = Math.min(1, Math.max(0, (evento.clientX - area.left) / area.width));
    player.currentTime = fracao * total;
    this.marqueAOnda(audio, player);
    marcador.style.left = (fracao * 100).toFixed(2) + '%';
  }

  /** Rótulo do tempo: quanto já tocou — parado no começo, a duração toda. */
  tempoDoAudio(audio: AudioDaBolha, player: HTMLAudioElement): string {
    const total = this.duracaoDoAudio(audio, player);
    const segundos = player.currentTime > 0 ? player.currentTime : total;
    return segundos > 0 ? this.emMinutosESegundos(segundos) : '';
  }

  private emMinutosESegundos(segundos: number): string {
    const inteiros = Math.max(0, Math.floor(segundos));
    const minutos = Math.floor(inteiros / 60);
    const resto = inteiros % 60;
    return `${minutos}:${resto < 10 ? '0' : ''}${resto}`;
  }

  /** Duração medida no arquivo decodificado; o <audio> serve de reserva. */
  private duracaoDoAudio(audio: AudioDaBolha, player: HTMLAudioElement): number {
    if (isFinite(player.duration) && player.duration > 0) return player.duration;
    return audio.duracaoSegundos;
  }

  /** Quanto da reprodução já passou, de 0 a 1 (0 = parado no começo). */
  private fracaoTocada(audio: AudioDaBolha, player: HTMLAudioElement): number {
    const total = this.duracaoDoAudio(audio, player);
    if (!total || player.currentTime <= 0) return 0;
    return Math.min(1, player.currentTime / total);
  }

  /** Uma onda de cada vez, na ordem em que as bolhas apareceram. */
  private enfileireAOnda(audio: AudioDaBolha) {
    this.filaDeOndas = this.filaDeOndas
      .then(() => this.calculeAOnda(audio.url))
      .then(decodificado => {
        // piso de altura só p/ o traço fino do silêncio não sumir do desenho
        audio.onda = decodificado.picos
          .map(pico => Math.max(ALTURA_MINIMA_DA_BARRA, Math.round(pico * 100)));
        if (decodificado.duracaoSegundos > 0) audio.duracaoSegundos = decodificado.duracaoSegundos;
      })
      .catch(() => {});
  }

  /**
   * Onda de verdade: baixa o arquivo, decodifica e tira o pico de cada fatia.
   * Normaliza pelo pico do próprio recado, senão fala baixa vira linha reta.
   * Formato que o navegador não decodifica (Safari com ogg/opus, por exemplo)
   * devolve vazio: a bolha mostra um traço liso em vez de desenhar uma onda
   * que não corresponde ao áudio.
   */
  private async calculeAOnda(url: string): Promise<{ picos: number[]; duracaoSegundos: number }> {
    const semOnda = { picos: [] as number[], duracaoSegundos: 0 };
    const Contexto = (window as any).OfflineAudioContext || (window as any).webkitOfflineAudioContext;
    if (!Contexto) return semOnda;

    try {
      const resposta = await fetch(url, { credentials: 'same-origin' });
      if (!resposta.ok) return semOnda;

      const arquivo = await resposta.arrayBuffer();
      if (arquivo.byteLength > LIMITE_PARA_DESENHAR_A_ONDA) return semOnda;

      // contexto offline em 8 kHz mono: o desenho não precisa de mais que isso,
      // e assim um recado longo não vira dezenas de MB de PCM na memória
      const contexto = new Contexto(1, 1, TAXA_PARA_DECODIFICAR);
      const buffer: AudioBuffer = await contexto.decodeAudioData(arquivo);
      const canal = buffer.getChannelData(0);
      const porFatia = Math.floor(canal.length / FATIAS_DA_ONDA) || 1;

      const picos: number[] = [];
      let maximo = 0;
      for (let fatia = 0; fatia < FATIAS_DA_ONDA; fatia++) {
        const inicio = fatia * porFatia;
        const fim = Math.min(inicio + porFatia, canal.length);
        let pico = 0;
        for (let i = inicio; i < fim; i++) {
          const amplitude = Math.abs(canal[i]);
          if (amplitude > pico) pico = amplitude;
        }
        picos.push(pico);
        if (pico > maximo) maximo = pico;
      }

      return {
        picos: maximo > 0 ? picos.map(pico => pico / maximo) : picos,
        duracaoSegundos: buffer.duration,
      };
    } catch {
      return semOnda;
    }
  }

  /**
   * A transcrição grava mensagem interativa de lista como corpo + linhas "• opção"
   * no final. Separa esse bloco para o template renderizar como cartão de opções
   * em vez de textão corrido.
   */
  private prepareOpcoesDaLista(turno: any) {
    turno.corpoExibicao = turno.texto;
    turno.opcoesLista = [];
    if (turno.papel !== 'mia') return;

    const linhas = String(turno.texto || '').split('\n');
    let inicio = linhas.length;
    while (inicio > 0 && /^\s*•\s+\S/.test(linhas[inicio - 1])) inicio--;

    const brutas = linhas.slice(inicio).map(linha => linha.replace(/^\s*•\s*/, '').trim());
    if (brutas.length < 2) return;

    // "Salmão batido — R$ 62,00" → título + preço p/ layout de linhas de menu
    const opcoes = brutas.map(opcao => {
      const partes = opcao.match(/^(.+?)\s*[—–]\s*((?:[^—–]*\bR\$\s?[\d.,]+)[^—–]*)$/);
      return partes ? { titulo: partes[1], preco: partes[2] } : { titulo: opcao, preco: '' };
    });

    turno.corpoExibicao = linhas.slice(0, inicio).join('\n').replace(/\s+$/, '');
    turno.opcoesLista = opcoes;
    // maioria com preço → vira menu (título esquerda, preço direita); senão, chips
    turno.opcoesComPreco = opcoes.filter(opcao => opcao.preco).length >= Math.ceil(opcoes.length / 2);
  }

  opcoesVisiveis(turno: any): Array<{ titulo: string; preco: string }> {
    const opcoes = turno.opcoesLista || [];
    if (opcoes.length <= MAXIMO_DE_OPCOES_SEM_COLAPSAR || turno.opcoesExpandidas) return opcoes;
    return opcoes.slice(0, OPCOES_VISIVEIS_COLAPSADO);
  }

  opcoesColapsaveis(turno: any): boolean {
    return (turno.opcoesLista || []).length > MAXIMO_DE_OPCOES_SEM_COLAPSAR;
  }

  alterneOpcoes(turno: any) {
    turno.opcoesExpandidas = !turno.opcoesExpandidas;
  }

  private labelDoDia(data: string | Date): string {
    const dia = new Date(data);
    const hoje = new Date();
    const ontem = new Date();
    ontem.setDate(hoje.getDate() - 1);

    if (dia.toDateString() === hoje.toDateString()) return 'Hoje';
    if (dia.toDateString() === ontem.toDateString()) return 'Ontem';
    return dia.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  }

  horaRelativa(data: string | Date): string {
    const momento = new Date(data);
    const hoje = new Date();
    const ontem = new Date();
    ontem.setDate(hoje.getDate() - 1);

    if (momento.toDateString() === hoje.toDateString()) {
      return momento.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    if (momento.toDateString() === ontem.toDateString()) return 'Ontem';
    return momento.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }

  nomeExibicao(conversa: any): string {
    return nomeDoContato(conversa?.nomeContato, conversa?.telefone);
  }

  telefoneDoContato(telefone: string): string {
    return telefoneParaCadastro(telefone);
  }

  formateTelefone(telefone: string): string {
    return formateTelefoneDaMia(telefone);
  }

  iniciais(conversa: any): string {
    return iniciaisDoContato(conversa?.nomeContato, conversa?.telefone);
  }

  corAvatar(conversa: any): string {
    return corDoAvatar(conversa?.telefone || conversa?.id);
  }

  /** Chip verde da lista: a conversa fechou pedido (vínculo GERADO do atendimento). */
  rotuloVendas(conversa: any): string {
    const pedidos = Number(conversa?.pedidosGerados) || 0;
    return pedidos > 1 ? `${pedidos} vendas` : 'venda';
  }

  tituloVendas(conversa: any): string {
    const pedidos = Number(conversa?.pedidosGerados) || 0;
    return pedidos > 1
      ? `Esta conversa fechou ${pedidos} pedidos`
      : 'Esta conversa fechou um pedido';
  }

  previewMensagem(conversa: any): string {
    const prefixo = conversa.ultimoPapel === 'mia' ? '🤖 ' : (conversa.ultimoPapel === 'atendente' ? '🎧 ' : '');
    return prefixo + (conversa.ultimaMensagem || '');
  }

  linkWhatsapp(conversa: any): string {
    const digitos = (conversa?.telefone || '').replace(/\D/g, '');
    return `https://wa.me/${digitos}`;
  }

  /** Soma tokens da conversa e junta os modelos que apareceram nos turnos. */
  private calculeResumoTelemetria(turnos: any[]) {
    const modelos: string[] = [];
    const porModelo: { [modelo: string]: { turnos: number; custoUsd: number } } = {};
    let entrada = 0, saida = 0, cache = 0, cacheEscrita = 0;
    let turnosMia = 0, turnosSemIA = 0;
    let tempoTotalMs = 0, turnosComTempo = 0;

    for (const turno of turnos || []) {
      if (turno.papel === 'mia' && turno.tempos?.totalMs != null) {
        tempoTotalMs += Number(turno.tempos.totalMs) || 0;
        turnosComTempo++;
      }
      if (turno.modelo && modelos.indexOf(turno.modelo) === -1) modelos.push(turno.modelo);
      entrada += Number(turno.tokensEntrada) || 0;
      saida += Number(turno.tokensSaida) || 0;
      cache += Number(turno.tokensCache) || 0;
      cacheEscrita += Number(turno.tokensCacheEscrita) || 0;

      if (turno.papel === 'mia') {
        turnosMia++;
        if (turno.modelo === 'atalho-categoria') turnosSemIA++;
      }
      if (turno.modelo && turno.modelo !== 'atalho-categoria') {
        const acumulado = porModelo[turno.modelo] || (porModelo[turno.modelo] = { turnos: 0, custoUsd: 0 });
        acumulado.turnos++;
        acumulado.custoUsd += Number(turno.custoUsd) || 0;
      }
    }

    return modelos.length || entrada || saida || turnosComTempo
      ? { modelos, entrada, saida, cache, cacheEscrita, turnosMia, turnosSemIA, porModelo, tempoTotalMs, turnosComTempo }
      : null;
  }

  /**
   * % dos tokens de entrada servidos pelo cache. Escrita conta como "não veio
   * do cache" — é justamente o token que pagou preço cheio (e mais 25%).
   */
  cacheHitDaConversa(): number | null {
    const resumo: any = this.resumoTelemetria;
    if (!resumo) return null;
    const total = resumo.entrada + resumo.cache + resumo.cacheEscrita;
    if (!total || !resumo.cache) return null;
    return Math.round((resumo.cache / total) * 100);
  }

  /** "3 de 12 sem IA" — quantos turnos da Mia saíram por atalho determinístico. */
  turnosSemIA(): string {
    const resumo: any = this.resumoTelemetria;
    if (!resumo?.turnosSemIA) return '';
    return `${resumo.turnosSemIA} de ${resumo.turnosMia} sem IA`;
  }

  /** Custo médio por turno de IA, com a quebra por modelo no tooltip. */
  custoMedioPorTurno(): string {
    const porModelo: any = (this.resumoTelemetria as any)?.porModelo || {};
    let turnos = 0, custo = 0;
    for (const modelo of Object.keys(porModelo)) {
      turnos += porModelo[modelo].turnos;
      custo += porModelo[modelo].custoUsd;
    }
    if (!turnos) return '';
    return `${this.custoEmReaisFormatado(custo / turnos)}/turno`;
  }

  tituloCustoMedio(): string {
    const porModelo: any = (this.resumoTelemetria as any)?.porModelo || {};
    const partes = Object.keys(porModelo).map(modelo => {
      const dados = porModelo[modelo];
      return `${this.rotuloModelo(modelo)}: ${this.custoEmReaisFormatado(dados.custoUsd / dados.turnos)}/turno em ${dados.turnos} turno(s)`;
    });
    return 'Custo médio por turno de IA · ' + partes.join(' · ');
  }

  /** Nome curto do modelo p/ caber no balão — o id completo fica no tooltip. */
  rotuloModelo(modelo: string): string {
    const id = String(modelo || '');
    if (!id) return '';
    if (id === 'atalho-categoria') return 'sem IA';
    return id.replace(/^claude-/, '').replace(/-\d{8}$/, '');
  }

  /** Modelos da conversa em rótulo curto (uma loja pode ter trocado no meio). */
  modelosDaConversa(): string {
    return (this.resumoTelemetria?.modelos || []).map(modelo => this.rotuloModelo(modelo)).join(' + ');
  }

  /**
   * "1593→279" (entrada→saída) do turno; vazio quando não houve chamada de IA.
   * Number exato aqui (como no simulador) — por turno cabe; o resumo do topo,
   * que soma a conversa toda, é que usa o formato compacto.
   */
  tokensDoTurno(turno: any): string {
    const entrada = Number(turno?.tokensEntrada) || 0;
    const saida = Number(turno?.tokensSaida) || 0;
    if (!entrada && !saida) return '';
    return `${entrada}→${saida}`;
  }

  /**
   * Cache do turno no badge (escrita + leitura somados) — sem ele o custo parece
   * errado: no 1º turno o system prompt inteiro é gravado no cache e domina o
   * valor, mas não aparece no "entrada→saída". O detalhe fica no tooltip.
   */
  cacheDoTurno(turno: any): string {
    const total = (Number(turno?.tokensCache) || 0) + (Number(turno?.tokensCacheEscrita) || 0);
    if (!total) return '';
    return `cache ${this.formateTokens(total)}`;
  }

  /** Milhares viram "32,4k" para o número não dominar a linha. */
  formateTokens(valor: number): string {
    const numero = Number(valor) || 0;
    if (numero < 1000) return String(numero);
    return `${(numero / 1000).toFixed(1).replace('.', ',')}k`;
  }

  custoEmReaisFormatado(valorUsd: number): string {
    return formateCustoEmReais(valorUsd);
  }

  /** Tempos do turno (espera/modelo/envio/total) vindos da rota admin; null em turno antigo sem rastreio. */
  temposDoTurno(turno: any): { esperaMs: number | null; modeloMs: number | null; envioMs: number | null; totalMs: number | null } | null {
    const tempos = turno?.tempos;
    if (!tempos) return null;
    return tempos.totalMs != null || tempos.esperaMs != null || tempos.envioMs != null ? tempos : null;
  }

  /** O que vai no selo da bolha: o total; sem envio registrado, o que já se sabe (espera + modelo). */
  tempoDoTurnoFormatado(turno: any): string {
    const tempos = this.temposDoTurno(turno);
    if (!tempos) return '';
    if (tempos.totalMs != null) return this.latenciaFormatada(tempos.totalMs);
    const parcial = (tempos.esperaMs || 0) + (tempos.modeloMs || 0);
    return parcial > 0 ? `${this.latenciaFormatada(parcial)}+` : '';
  }

  tituloTempos(turno: any): string {
    const tempos = this.temposDoTurno(turno);
    if (!tempos) return '';
    const partes: string[] = [];
    if (tempos.esperaMs != null) partes.push(`espera ${this.latenciaFormatada(tempos.esperaMs)} (agrupamento das mensagens + chegada na Mia)`);
    if (tempos.modeloMs != null) partes.push(`modelo ${this.latenciaFormatada(tempos.modeloMs)}`);
    if (tempos.envioMs != null) partes.push(`envio ${this.latenciaFormatada(tempos.envioMs)}`);
    partes.push(tempos.totalMs != null
      ? `total ${this.latenciaFormatada(tempos.totalMs)} da mensagem do cliente até a resposta sair`
      : 'envio ainda não registrado');
    return partes.join(' · ');
  }

  /** Total acima do limite ganha o mesmo destaque da latência lenta. */
  tempoLento(turno: any): boolean {
    const tempos = this.temposDoTurno(turno);
    return !!tempos && (Number(tempos.totalMs) || 0) >= TEMPO_TOTAL_LENTO_MS;
  }

  linhasDeTempo(turno: any): Array<{ rotulo: string; valor: string }> {
    const tempos = this.temposDoTurno(turno);
    if (!tempos) return [];
    const linhas: Array<{ rotulo: string; valor: string }> = [];
    if (tempos.esperaMs != null) linhas.push({ rotulo: 'Espera (agrupamento + chegada)', valor: this.latenciaFormatada(tempos.esperaMs) });
    if (tempos.modeloMs != null) linhas.push({ rotulo: 'Modelo', valor: this.latenciaFormatada(tempos.modeloMs) });
    if (tempos.envioMs != null) linhas.push({ rotulo: 'Envio ao WhatsApp', valor: this.latenciaFormatada(tempos.envioMs) });
    if (tempos.totalMs != null) linhas.push({ rotulo: 'Total até a resposta sair', valor: this.latenciaFormatada(tempos.totalMs) });
    return linhas;
  }

  /** Média do tempo total nos turnos da conversa que têm rastreio; vazio sem nenhum. */
  tempoMedioDaConversa(): string {
    const resumo: any = this.resumoTelemetria;
    if (!resumo?.turnosComTempo) return '';
    return this.latenciaFormatada(Math.round(resumo.tempoTotalMs / resumo.turnosComTempo));
  }

  tituloTempoMedio(): string {
    const resumo: any = this.resumoTelemetria;
    if (!resumo?.turnosComTempo) return '';
    return `Tempo médio da mensagem do cliente até a resposta sair, em ${resumo.turnosComTempo} turno(s) com rastreio`;
  }

  tituloCustoEmReais(): string {
    return 'Custo estimado em reais · PTAX venda de 28/07/2026: US$ 1 = R$ 5,1177';
  }

  tituloTelemetria(turno: any): string {
    if (!turno?.modelo) return '';
    const partes = [
      turno.modelo,
      `${turno.tokensEntrada || 0} de entrada`,
      `${turno.tokensSaida || 0} de saída`,
    ];
    const cacheEscrita = Number(turno.tokensCacheEscrita) || 0;
    const cacheLeitura = Number(turno.tokensCache) || 0;
    if (cacheEscrita) partes.push(`${cacheEscrita} gravados no cache (1,25× o preço de entrada)`);
    if (cacheLeitura) partes.push(`${cacheLeitura} lidos do cache`);
    if (turno.custoUsd != null) partes.push(this.custoEmReaisFormatado(turno.custoUsd));
    if (turno.latenciaMs != null) partes.push(`${turno.latenciaMs}ms`);
    return partes.join(' · ') + ' · PTAX venda 28/07/2026';
  }

  /** Latência entra no badge só quando estourou o limite — o resto fica no detalhe. */
  latenciaLenta(turno: any): boolean {
    return (Number(turno?.latenciaMs) || 0) >= LATENCIA_LENTA_MS;
  }

  latenciaFormatada(ms: number): string {
    const valor = Number(ms) || 0;
    if (valor < 1000) return `${valor}ms`;
    return `${(valor / 1000).toFixed(1).replace('.', ',')}s`;
  }

  alterneDetalheCusto(turno: any) {
    // atalho não tem custo para quebrar; abrir o detalhe só confundiria
    if (turno?.papel !== 'mia' || !turno?.modelo || turno.modelo === 'atalho-categoria') return;
    turno.detalheCustoAberto = !turno.detalheCustoAberto;
  }

  /** Snapshot de preços gravado com o turno; null em turnos antigos (sem quebra confiável). */
  private precosDoTurno(turno: any): { entrada: number; saida: number; cache: number; cacheEscrita: number } | null {
    if (!turno?.precosUsdMtok) return null;
    try {
      const precos = JSON.parse(turno.precosUsdMtok);
      return typeof precos?.entrada === 'number' ? precos : null;
    } catch {
      return null;
    }
  }

  /**
   * Quebra do custo do turno nos 4 componentes, cada um com tokens × preço do
   * snapshot — por isso a soma sempre fecha com o custoUsd gravado, mesmo que
   * a tabela de preços de hoje seja outra.
   */
  parcelasDoTurno(turno: any): Array<{ rotulo: string; tokens: number; precoMtok: number; usd: number; pct: number }> | null {
    const precos = this.precosDoTurno(turno);
    if (!precos) return null;

    const linhas = [
      { rotulo: 'Entrada (sem cache)', tokens: Number(turno.tokensEntrada) || 0, precoMtok: precos.entrada },
      { rotulo: 'Gravação de cache', tokens: Number(turno.tokensCacheEscrita) || 0, precoMtok: precos.cacheEscrita },
      { rotulo: 'Leitura de cache', tokens: Number(turno.tokensCache) || 0, precoMtok: precos.cache },
      { rotulo: 'Saída', tokens: Number(turno.tokensSaida) || 0, precoMtok: precos.saida },
    ].filter(linha => linha.tokens > 0);

    const total = linhas.reduce((soma, linha) => soma + linha.tokens * linha.precoMtok, 0) / 1_000_000;
    return linhas.map(linha => {
      const usd = (linha.tokens * linha.precoMtok) / 1_000_000;
      return { ...linha, usd, pct: total ? Math.round((usd / total) * 100) : 0 };
    });
  }

  /** Contrafactual: todos os tokens de entrada a preço cheio, sem cache algum. */
  custoSemCacheUsd(turno: any): number | null {
    const precos = this.precosDoTurno(turno);
    if (!precos) return null;
    const entradaTotal = (Number(turno.tokensEntrada) || 0) + (Number(turno.tokensCache) || 0) +
      (Number(turno.tokensCacheEscrita) || 0);
    if (!entradaTotal) return null;
    return (entradaTotal * precos.entrada + (Number(turno.tokensSaida) || 0) * precos.saida) / 1_000_000;
  }

  formateUsd(valor: number): string {
    const numero = Number(valor) || 0;
    return 'US$ ' + numero.toFixed(numero >= 0.01 ? 4 : 5).replace('.', ',');
  }

  formatePrecoMtok(valor: number): string {
    return '$' + (Number(valor) || 0).toFixed(2).replace('.', ',') + '/MTok';
  }

  tituloStatusWhatsapp(status: string): string {
    switch (status) {
      case 'sent': return 'Enviada';
      case 'delivered': return 'Entregue';
      case 'read': return 'Visualizada';
      case 'failed': return 'Falha no envio';
      default: return '';
    }
  }
}
